<script setup>
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';
import { useTrack } from 'dashboard/composables';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { COPILOT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

import Button from 'dashboard/components-next/button/Button.vue';
import CopilotFontes from './CopilotFontes.vue';
import CopilotAvaliacao from './CopilotAvaliacao.vue';
import CopilotAcao from './CopilotAcao.vue';

const props = defineProps({
  isLastMessage: {
    type: Boolean,
    default: false,
  },
  message: {
    type: Object,
    required: true,
  },
  conversationInboxType: {
    type: String,
    required: true,
  },
  // Segundos (`created_at` do servidor): a hora dos dados das fontes.
  criadoEm: {
    type: Number,
    default: null,
  },
  // Para o 👍/👎: qual mensagem, de qual conversa.
  mensagemId: {
    type: Number,
    default: null,
  },
  threadId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['useSuggestion', 'mostrar']);

// O "Me mostra" (guia do catálogo do servidor, Captain::Copilot::Guias).
const guia = computed(() => props.message?.guia || null);

// A PROPOSTA (Fase 3, 08/10/2026): o Lumos propõe, a pessoa faz. Hoje só
// "agendar a partir de uma publicação": o clique abre Publicações com a ficha
// e o painel Agendar já preenchido — nada é gravado daqui.
const router = useRouter();
const route = useRoute();
const proposta = computed(() =>
  props.message?.proposta?.tipo === 'agendar_publicacao'
    ? props.message.proposta
    : null
);
// O painel do Lumos sai da frente: em Publicações ele cobria o painel
// Agendar, justo o que a pessoa foi revisar (visto na foto, 08/10/2026).
const { updateUISettings } = useUISettings();
const revisarProposta = () => {
  updateUISettings({ is_copilot_panel_open: false });
  router.push({
    name: 'legal_publications_index',
    params: { accountId: route.params.accountId },
    query: { publicacao: String(proposta.value.id), agendar: '1' },
  });
};

const cartoes = computed(() => props.message?.cartoes || []);
// A secretária (21/10/2026): as ações que o Copiloto propôs neste turno, cada uma com o seu cartão de confirmar.
const acoes = computed(() => props.message?.acoes || []);
// Próximas perguntas só na última resposta: nas antigas, a conversa já
// seguiu por outro caminho.
const sugestoes = computed(() =>
  props.isLastMessage ? props.message?.sugestoes || [] : []
);
const hasEmptyMessageContent = computed(() => !props.message?.content);

const showUseButton = computed(() => {
  return (
    !hasEmptyMessageContent.value &&
    props.message.reply_suggestion &&
    props.isLastMessage
  );
});

const messageContent = computed(() => {
  const formatter = new MessageFormatter(props.message.content);
  return formatter.formattedMessage;
});

const insertIntoRichEditor = computed(() => {
  return [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL].includes(
    props.conversationInboxType
  );
});

const useCopilotResponse = () => {
  if (insertIntoRichEditor.value) {
    emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, props.message?.content);
  } else {
    emitter.emit(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, props.message?.content);
  }
  useTrack(COPILOT_EVENTS.USE_CAPTAIN_RESPONSE);
};
</script>

<template>
  <div class="flex items-end justify-start gap-2">
    <div
      class="flex-shrink-0 w-7 h-7 rounded-full bg-woot-600 flex items-center justify-center"
    >
      <span class="i-ph-robot w-4 h-4 text-white" />
    </div>
    <div class="flex flex-col gap-1 max-w-[85%]">
      <div
        class="px-4 py-2.5 text-sm leading-relaxed bg-n-solid-2 border border-n-container text-n-slate-12 rounded-bl-sm rounded-br-xl rounded-t-xl break-words"
      >
        <span v-if="hasEmptyMessageContent" class="text-n-ruby-11">
          {{ $t('CAPTAIN.COPILOT.EMPTY_MESSAGE') }}
        </span>
        <div v-else v-dompurify-html="messageContent" class="prose-sm" />
      </div>
      <button
        v-if="proposta"
        type="button"
        class="lumos-mostra lumos-mostra--proposta"
        @click="revisarProposta"
      >
        <span class="lumos-mostra__ladrilho" aria-hidden="true">
          <i class="i-ph-calendar-plus-fill" />
        </span>
        <span class="lumos-mostra__texto">
          <span class="lumos-mostra__rotulo">
            {{ $t('CAPTAIN.COPILOT.PROPOSTA.AGENDAR') }}
          </span>
          <span class="lumos-mostra__titulo">{{ proposta.titulo }}</span>
          <span v-if="proposta.sub" class="lumos-mostra__sub">
            {{ proposta.sub }}
          </span>
        </span>
        <i class="i-ph-arrow-right lumos-mostra__seta" aria-hidden="true" />
      </button>
      <button
        v-if="guia"
        type="button"
        class="lumos-mostra"
        @click="emit('mostrar', guia)"
      >
        <span class="lumos-mostra__ladrilho" aria-hidden="true">
          <i class="i-ph-cursor-click-fill" />
        </span>
        <span class="lumos-mostra__texto">
          <span class="lumos-mostra__rotulo">
            {{ $t('CAPTAIN.COPILOT.ME_MOSTRA') }}
          </span>
          <span class="lumos-mostra__titulo">{{ guia.titulo }}</span>
        </span>
        <i class="i-ph-arrow-right lumos-mostra__seta" aria-hidden="true" />
      </button>
      <CopilotAcao
        v-for="acao in acoes"
        :key="`acao-${acao.id}`"
        :acao="acao"
      />
      <CopilotFontes
        v-if="cartoes.length"
        :cartoes="cartoes"
        :criado-em="criadoEm"
      />
      <CopilotAvaliacao
        v-if="mensagemId && threadId && !hasEmptyMessageContent"
        :key="`aval-${mensagemId}`"
        :mensagem-id="mensagemId"
        :thread-id="threadId"
        :avaliacao="message.avaliacao || null"
      />
      <div
        v-if="sugestoes.length"
        class="lumos-sugestoes"
        role="group"
        :aria-label="$t('CAPTAIN.COPILOT.SUGESTOES')"
      >
        <button
          v-for="(sugestao, i) in sugestoes"
          :key="sugestao"
          type="button"
          class="lumos-sugestao"
          :style="{ '--i': i }"
          @click="emit('useSuggestion', sugestao)"
        >
          {{ sugestao }}
        </button>
      </div>
      <div v-if="showUseButton" class="flex flex-row px-1">
        <Button
          :label="$t('CAPTAIN.COPILOT.USE')"
          faded
          sm
          slate
          @click="useCopilotResponse"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* As próximas perguntas: pílula na cor da IA (íris), que a pessoa toca em
   vez de digitar. Chegam depois da resposta, uma a uma. */
.lumos-sugestoes {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
  padding-top: var(--esp-1);
}

.lumos-sugestao {
  min-height: 32px;
  padding: var(--esp-1) var(--esp-3);
  border: 0;
  border-radius: var(--r-redondo);
  background: rgb(var(--iris-9) / 8%);
  box-shadow: inset 0 0 0 1px rgb(var(--iris-9) / 22%);
  font-size: var(--txt-corpo);
  font-weight: var(--peso-dado);
  line-height: var(--linha-apertada);
  color: rgb(var(--iris-11));
  text-align: start;
  cursor: pointer;
  animation: lumos-sugestao-chega var(--mov-3) var(--mov-curva)
    calc(var(--mov-1) + var(--i, 0) * var(--cascata-passo)) backwards;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva);
}

.lumos-sugestao:hover {
  background: rgb(var(--iris-9));
  color: rgb(255 255 255);
  transform: translateY(-1px);
}

.lumos-sugestao:active {
  transform: none;
}

.lumos-sugestao:focus-visible {
  outline: 2px solid rgb(var(--iris-9));
  outline-offset: 2px;
}

/* O "Me mostra": a ação principal da resposta quando a pergunta é COMO se
   faz. Botão forte da marca (o mesmo degradê do .advup-btn-forte não cabe
   aqui: é um cartão com título), ladrilho cheio e seta que anda. */
.lumos-mostra {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  width: 100%;
  min-height: 52px;
  padding: var(--esp-2) var(--esp-3);
  border: 0;
  border-radius: var(--r-4);
  background: rgb(var(--blue-9) / 7%);
  box-shadow: inset 0 0 0 1px rgb(var(--blue-9) / 25%);
  color: rgb(var(--slate-12));
  text-align: start;
  cursor: pointer;
  animation: lumos-sugestao-chega var(--mov-3) var(--mov-curva) backwards;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    box-shadow var(--mov-2) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva);
}

.lumos-mostra:hover {
  background: rgb(var(--blue-9) / 12%);
  box-shadow:
    inset 0 0 0 1px rgb(var(--blue-9) / 45%),
    0 6px 14px -8px rgb(var(--blue-9) / 50%);
  transform: translateY(-1px);
}

.lumos-mostra:active {
  transform: none;
}

.lumos-mostra:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.lumos-mostra__ladrilho {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: var(--r-3);
  background: rgb(var(--blue-9));
  color: rgb(255 255 255);
  box-shadow: 0 3px 8px -3px rgb(var(--blue-9) / 60%);
}

.lumos-mostra__ladrilho > i {
  font-size: var(--ico-2);
}

.lumos-mostra__texto {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 0;
}

.lumos-mostra__rotulo {
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--blue-11));
}

.lumos-mostra__titulo {
  font-size: var(--txt-dado);
  font-weight: var(--peso-dado);
  line-height: var(--linha-normal);
}

.lumos-mostra__sub {
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-11));
}

/* A proposta pede a pessoa: âmbar, a cor do "vai pedir você" (o mesmo tom
   das publicações que pedem ação). O glifo é escuro — branco sobre âmbar-9
   dá 1,9:1. */
.lumos-mostra--proposta {
  background: rgb(var(--amber-9) / 9%);
  box-shadow: inset 0 0 0 1px rgb(var(--amber-9) / 35%);
}

.lumos-mostra--proposta:hover {
  background: rgb(var(--amber-9) / 15%);
  box-shadow:
    inset 0 0 0 1px rgb(var(--amber-9) / 55%),
    0 6px 14px -8px rgb(var(--amber-9) / 55%);
}

.lumos-mostra--proposta .lumos-mostra__ladrilho {
  background: rgb(var(--amber-9));
  color: rgb(42 26 0);
  box-shadow: 0 3px 8px -3px rgb(var(--amber-9) / 60%);
}

.lumos-mostra--proposta .lumos-mostra__rotulo,
.lumos-mostra--proposta .lumos-mostra__seta {
  color: rgb(var(--amber-11));
}

.lumos-mostra__seta {
  flex-shrink: 0;
  font-size: var(--ico-2);
  color: rgb(var(--blue-10));
  transition: transform var(--mov-2) var(--mov-curva);
}

.lumos-mostra:hover .lumos-mostra__seta {
  transform: translateX(3px);
}

@keyframes lumos-sugestao-chega {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
}
</style>
